import { z } from 'zod'

/**
 * People domain logic — shared verbatim by API validation/normalization, web
 * and mobile (02-PHASES.md, Phase 2 shared packages).
 */

// ---- names -----------------------------------------------------------------

export interface PersonNameParts {
  firstName: string
  lastName: string
  nickname?: string | null
  prefix?: string | null
  suffix?: string | null
}

/** "Rev. Jimmy (Jim) Smith Jr." with every part optional except first/last. */
export function formatFullName(p: PersonNameParts, opts?: { formal?: boolean }): string {
  const first = !opts?.formal && p.nickname ? p.nickname : p.firstName
  return [opts?.formal ? p.prefix : null, first, p.lastName, opts?.formal ? p.suffix : null]
    .filter((part): part is string => !!part && part.trim() !== '')
    .join(' ')
}

/** "Smith, Jim" — directory sort display. */
export function formatSortName(p: PersonNameParts): string {
  return `${p.lastName}, ${p.nickname ?? p.firstName}`
}

// ---- phones ----------------------------------------------------------------

/**
 * Normalizes to E.164 or returns null for input we can't confidently parse.
 * Not a libphonenumber replacement — churches enter US-style numbers with
 * punctuation, plus the occasional already-international one. Anything
 * ambiguous is rejected rather than guessed into the wrong country.
 */
export function normalizePhoneE164(input: string, defaultCountry: 'US' = 'US'): string | null {
  const trimmed = input.trim()
  if (trimmed === '') return null

  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')

  if (hasPlus) {
    // Already international: E.164 allows up to 15 digits, min ~8 in practice.
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null
  }

  if (defaultCountry === 'US') {
    if (digits.length === 10) return `+1${digits}`
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  }
  return null
}

// ---- dates -----------------------------------------------------------------

/** Age in whole years on a given date; null when the birthdate is unknown. */
export function ageOn(birthdateIso: string | null | undefined, onIso: string): number | null {
  if (!birthdateIso) return null
  const birth = new Date(`${birthdateIso}T00:00:00Z`)
  const on = new Date(`${onIso}T00:00:00Z`)
  if (Number.isNaN(birth.getTime()) || Number.isNaN(on.getTime()) || on < birth) return null

  let age = on.getUTCFullYear() - birth.getUTCFullYear()
  const hadBirthday =
    on.getUTCMonth() > birth.getUTCMonth() ||
    (on.getUTCMonth() === birth.getUTCMonth() && on.getUTCDate() >= birth.getUTCDate())
  if (!hadBirthday) age -= 1
  return age
}

/** Child-safety gate (check-ins, minors' data visibility). */
export function isMinorOn(birthdateIso: string | null | undefined, onIso: string): boolean {
  const age = ageOn(birthdateIso, onIso)
  // Unknown birthdate ⇒ NOT treated as minor; the minors permission gates
  // *known* minors. (Directory imports rarely include adult birthdates.)
  return age !== null && age < 18
}

/** What says whether someone is a child: a birthdate, the church's own flag, or both. */
export interface MinorSignals {
  birthdate?: string | null
  /** `people.child` — the church's answer for a record with no birthdate. */
  child?: boolean | null
}

/**
 * A minor: flagged as a child, or under 18 by their birthdate. The flag is
 * what makes a child imported without a birthday a child; a birthdate alone
 * still decides it when nobody set the flag.
 */
export function isMinor(person: MinorSignals, onIso: string): boolean {
  return person.child === true || isMinorOn(person.birthdate, onIso)
}

/**
 * The calendar date it currently is in the *latest* timezone on Earth
 * (UTC−12). A minor is a minor until their 18th birthday has arrived
 * everywhere, so a server that does not know the reader's timezone asks
 * "what day is it at the furthest point behind?" — the answer that errs
 * towards hiding a child's details for a few extra hours, never towards
 * showing them early.
 */
export function minorCutoffDate(now: Date = new Date()): string {
  return new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/**
 * Whether a reader must not see this person's own phone, email and address.
 * Only *known* minors are gated (see `isMinorOn`); the person's name, photo
 * and household stay visible — the gate is on how to reach a child directly.
 */
export function hidesMinorContacts(
  person: MinorSignals | string | null | undefined,
  canReadMinors: boolean,
  onIso: string = minorCutoffDate(),
): boolean {
  const signals = typeof person === 'object' && person !== null ? person : { birthdate: person }
  return !canReadMinors && isMinor(signals, onIso)
}

// ---- school grades -----------------------------------------------------------

/** "08-01": the default day a school year starts and children move up. */
export const DEFAULT_SCHOOL_YEAR_STARTS = '08-01'

const MONTH_DAY = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/

/** A valid "MM-DD", or null. */
export function parseMonthDay(value: string | null | undefined): string | null {
  return value && MONTH_DAY.test(value) ? value : null
}

/**
 * The calendar year the school year running on `onIso` ends in. With a
 * 1 August start, 15 October 2026 is in the 2026–27 year (ends 2027) and
 * 15 July 2027 still is.
 */
export function schoolYearEnding(
  onIso: string,
  startsMonthDay = DEFAULT_SCHOOL_YEAR_STARTS,
): number {
  const year = Number(onIso.slice(0, 4))
  const starts = parseMonthDay(startsMonthDay) ?? DEFAULT_SCHOOL_YEAR_STARTS
  return onIso.slice(5, 10) >= starts ? year + 1 : year
}

/**
 * The grade someone is in on a date, from the year they finish school
 * (grade 12). Kindergarten is 0 and pre-K below it, so a toddler's grade is
 * negative and a graduate's is past 12 — the check-in rules compare numbers,
 * and `gradeLabel` names them.
 */
export function gradeFor(
  graduationYear: number | null | undefined,
  onIso: string,
  startsMonthDay = DEFAULT_SCHOOL_YEAR_STARTS,
): number | null {
  if (graduationYear === null || graduationYear === undefined) return null
  return 12 - (graduationYear - schoolYearEnding(onIso, startsMonthDay))
}

/** The other way round: the grade someone is in now → the year they finish. */
export function graduationYearFor(
  grade: number,
  onIso: string,
  startsMonthDay = DEFAULT_SCHOOL_YEAR_STARTS,
): number {
  return schoolYearEnding(onIso, startsMonthDay) + 12 - grade
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/** "08-01" → "1 August". */
export function formatMonthDay(value: string | null | undefined): string | null {
  const valid = parseMonthDay(value)
  if (!valid) return null
  return `${Number(valid.slice(3, 5))} ${MONTH_NAMES[Number(valid.slice(0, 2)) - 1] ?? ''}`
}

/** The grades a person can be put in, youngest first, for pickers. */
export const GRADE_CHOICES = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const

/** "Pre-K", "Kindergarten", "3rd grade", "Graduated". */
export function gradeLabel(grade: number | null | undefined): string | null {
  if (grade === null || grade === undefined) return null
  if (grade < 0) return 'Pre-K'
  if (grade === 0) return 'Kindergarten'
  if (grade > 12) return 'Graduated'
  const suffix =
    grade % 10 === 1 && grade !== 11
      ? 'st'
      : grade % 10 === 2 && grade !== 12
        ? 'nd'
        : grade % 10 === 3 && grade !== 13
          ? 'rd'
          : 'th'
  return `${grade}${suffix} grade`
}

// ---- schemas ---------------------------------------------------------------

export const MEMBERSHIP_STATUSES = ['visitor', 'regular', 'member', 'inactive'] as const
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number]

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  // A round trip, not just a NaN check: `new Date('2000-02-30')` rolls over to
  // March 1st rather than failing, so the shape test alone accepts a day that
  // does not exist and stores it as a different one.
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`)
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  }, 'Not a real date')

const nameField = (which: string) => z.string().trim().min(1, `Enter a ${which}`).max(200)
// `.nullish().transform` rather than `z.preprocess`: preprocess types its
// input as `unknown`, which poisons react-hook-form's resolver generics.
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v))

export const emailEntrySchema = z.object({
  email: z.email('Enter a valid email address').max(320),
  label: optionalText(50),
  isPrimary: z.boolean().optional(),
})

export const phoneEntrySchema = z.object({
  number: z
    .string()
    .max(30)
    .refine((v) => normalizePhoneE164(v) !== null, 'Enter a valid phone number'),
  label: optionalText(50),
  isPrimary: z.boolean().optional(),
})

export const addressEntrySchema = z.object({
  street: optionalText(200),
  street2: optionalText(200),
  city: optionalText(100),
  state: optionalText(100),
  postalCode: optionalText(20),
  country: optionalText(100),
  label: optionalText(50),
  isPrimary: z.boolean().optional(),
})

export const personCoreSchema = z.object({
  firstName: nameField('first name'),
  lastName: nameField('last name'),
  middleName: optionalText(200),
  nickname: optionalText(200),
  prefix: optionalText(50),
  suffix: optionalText(50),
  gender: optionalText(50),
  birthdate: isoDate.nullish(),
  anniversary: isoDate.nullish(),
  maritalStatus: optionalText(50),
  membershipStatus: z.enum(MEMBERSHIP_STATUSES).optional(),
  campusId: z.uuid().nullish(),
  /** The year they finish school; the grade is worked out from it. */
  graduationYear: z.number().int().min(1900).max(2200).nullish(),
  /** Or the grade they are in today (−1 pre-K, 0 kindergarten): stored as the year. */
  grade: z.number().int().min(-1).max(13).nullish(),
  child: z.boolean().optional(),
  allergies: optionalText(500),
  medicalNotes: optionalText(1000),
  specialNeeds: optionalText(1000),
  emergencyContactName: optionalText(200),
  emergencyContactPhone: z
    .string()
    .trim()
    .max(30)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v))
    .refine((v) => v === null || normalizePhoneE164(v) !== null, 'Enter a valid phone number'),
})

export const createPersonSchema = personCoreSchema.extend({
  emails: z.array(emailEntrySchema).max(10).optional(),
  phones: z.array(phoneEntrySchema).max(10).optional(),
  addresses: z.array(addressEntrySchema).max(10).optional(),
})
export type CreatePersonInput = z.infer<typeof createPersonSchema>
/** What the form holds *before* transforms — react-hook-form's TFieldValues. */
export type CreatePersonFormInput = z.input<typeof createPersonSchema>

export const updatePersonSchema = personCoreSchema.partial()
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>
export type UpdatePersonFormInput = z.input<typeof updatePersonSchema>

export const contactsSchema = z.object({
  emails: z.array(emailEntrySchema).max(10),
  phones: z.array(phoneEntrySchema).max(10),
  addresses: z.array(addressEntrySchema).max(10),
})
export type ContactsInput = z.infer<typeof contactsSchema>
export type ContactsFormInput = z.input<typeof contactsSchema>
